import axiosInstance from '../lib/axios';

export interface Permission {
  module: string;
  action: string;
  value: boolean;
}

export interface ModulePermissions {
  [action: string]: boolean;
}

export interface UserPermissions {
  employee_management: {
    employees: ModulePermissions;
    department: ModulePermissions;
    agreement: ModulePermissions;
    compensation: ModulePermissions;
  };
  sales_management: {
    client: ModulePermissions;
    proposal: ModulePermissions;
    contract: ModulePermissions;
    revenue: ModulePermissions;
  };
  finance_management: {
    invoice: ModulePermissions;
    expense: ModulePermissions;
    financialReport: ModulePermissions;
  };
  hiring: {
    candidate: ModulePermissions;
    jobPosting: ModulePermissions;
    onboarding: ModulePermissions;
    settings: {
      onboardingTask: ModulePermissions;
    };
  };
  project_management: {
    project: ModulePermissions; // Project management permissions
    task: ModulePermissions; // Task management permissions
    projectStatus: ModulePermissions; // Status Settings permissions
  };
  settings: {
    roleAndPermission: ModulePermissions;
    jobTitle: ModulePermissions;
  };
  system: {
    system: ModulePermissions;
  };
}

export interface PermissionResponse {
  statusCode: number;
  message: string;
  data: {
    accessToken: string;
    refreshToken: string;
    user: {
      _id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      permissions: UserPermissions;
    };
  };
}

export interface GroupedPermissionsResponse {
  [module: string]: string[];
}

interface PermissionsResponse {
  success?: boolean;
  permissions?: UserPermissions;
  groups?: UserPermissions;
  roles?: string[];
  sales_clients?: boolean;
  project_management?: boolean;
  finance_management?: boolean;
}

export const permissionService = {
  // Get all permissions grouped by module
  getAllPermissions: async (): Promise<GroupedPermissionsResponse> => {
    const response = await axiosInstance.get<GroupedPermissionsResponse>('/permissions');
    return response.data;
  },

  // Get permissions for a specific role
  getRolePermissions: async (roleId: string): Promise<UserPermissions | null> => {
    try {
      const response = await axiosInstance.get<UserPermissions>(`/permissions/role/${roleId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403) {
        console.warn('⚠️ User does not have permission to view role permissions');
        return null;
      }
      throw error;
    }
  },

  // Fetch user permissions from the separate API endpoint
  getUserPermissions: async (): Promise<{ permissions: UserPermissions; roles: string[]; sales_clients?: boolean; project_management?: boolean; finance_management?: boolean }> => {
    try {
      const response = await axiosInstance.get<PermissionsResponse>('/employees/permissions');

      // The backend should now return permissions in the correct format
      let permissions = response.data.groups || response.data.permissions || {
        employee_management: {
          employees: { view: false, create: false, edit: false, delete: false },
          department: { view: false, create: false, edit: false, delete: false },
          agreement: { view: false, edit: false },
          compensation: { view: false, edit: false }
        },
        sales_management: {
          client: { view: false, create: false, edit: false, delete: false },
          proposal: { view: false, create: false, edit: false, delete: false },
          contract: { view: false, create: false, edit: false, delete: false },
          revenue: { view: false }
        },
        finance_management: {
          invoice: { view: false, create: false, edit: false, delete: false },
          expense: { view: false, create: false, edit: false, delete: false },
          financialReport: { view: false }
        },
        hiring: {
          candidate: { view: false, create: false, edit: false, delete: false },
          jobPosting: { view: false, create: false, edit: false, delete: false },
          onboarding: { view: false, create: false, edit: false, delete: false },
          settings: {
            onboardingTask: { view: false, create: false, edit: false, delete: false }
          }
        },
        project_management: {
          project: { view: false, create: false, edit: false, delete: false },
          task: { view: false, create: false, edit: false, delete: false },
          projectStatus: { view: false, create: false, edit: false, delete: false }
        },
        settings: {
          roleAndPermission: { view: false, create: false, edit: false, delete: false },
          jobTitle: { view: false, create: false, edit: false, delete: false },
        },
        system: {
          system: { admin: false }
        }
      };

      // Normalize sales_clients structure from backend to sales_management used in frontend
      const permissionsAsAny = permissions as any;
      if (permissionsAsAny.sales_clients) {
        const salesClientsPermissions = permissionsAsAny.sales_clients;

        if (permissions.sales_management) {
          permissions.sales_management = {
            ...permissions.sales_management,
            ...salesClientsPermissions
          };
        } else {
          permissions.sales_management = salesClientsPermissions;
        }

        delete permissionsAsAny.sales_clients;
      }

      return {
        permissions: permissions,
        roles: response.data.roles || [],
        sales_clients: response.data.sales_clients ?? (permissions.sales_management ? true : undefined),
        project_management: response.data.project_management ?? (permissions.project_management ? true : undefined),
        finance_management: response.data.finance_management ?? (permissions.finance_management ? true : undefined)
      };
    } catch (error: any) {
      throw error;
    }
  },

  // Refresh user permissions (useful when permissions change)
  refreshPermissions: async (): Promise<{ permissions: UserPermissions; roles: string[] }> => {
    return permissionService.getUserPermissions();
  },

  // Check if user has a specific permission
  hasPermission: (permissions: UserPermissions, permission: string): boolean => {
    if (!permissions) return false;

    const [module, subModule, action] = permission.toLowerCase().split('.');

    if (module && subModule && action) {
      return Boolean((permissions as any)?.[module]?.[subModule]?.[action]);
    } else if (module && action) {
      return Boolean((permissions as any)?.[module]?.[action]);
    }

    return false;
  },

  // Check if user has module permission
  hasModulePermission: (permissions: UserPermissions, module: string, action: string): boolean => {
    if (!permissions) return false;

    const modulePermissions = (permissions as any)?.[module];
    if (!modulePermissions) {
      return false;
    }

    // Check if any submodule has the required action permission
    return Object.values(modulePermissions).some((submodule: any) => {
      return submodule[action] === true;
    });
  },

  // Convert API permissions to flat permission array
  convertPermissionsToArray: (permissions: UserPermissions): string[] => {
    const permissionArray: string[] = [];

    // Employee management permissions
    if (permissions.employee_management) {
      Object.entries(permissions.employee_management).forEach(([module, actions]) => {
        Object.entries(actions).forEach(([action, value]) => {
          if (value) {
            permissionArray.push(`${module.toUpperCase()}_${action.toUpperCase()}`);
          }
        });
      });
    }

    // Sales management permissions
    if (permissions.sales_management) {
      Object.entries(permissions.sales_management).forEach(([module, actions]) => {
        Object.entries(actions).forEach(([action, value]) => {
          if (value) {
            permissionArray.push(`${module.toUpperCase()}_${action.toUpperCase()}`);
          }
        });
      });
    }

    // Finance management permissions
    if (permissions.finance_management) {
      Object.entries(permissions.finance_management).forEach(([module, actions]) => {
        Object.entries(actions).forEach(([action, value]) => {
          if (value) {
            permissionArray.push(`${module.toUpperCase()}_${action.toUpperCase()}`);
          }
        });
      });
    }

    // Hiring permissions
    if (permissions.hiring) {
      Object.entries(permissions.hiring).forEach(([module, actions]) => {
        Object.entries(actions).forEach(([action, value]) => {
          if (value) {
            permissionArray.push(`${module.toUpperCase()}_${action.toUpperCase()}`);
          }
        });
      });
    }

    // Settings permissions
    if (permissions.settings) {
      Object.entries(permissions.settings).forEach(([module, actions]) => {
        Object.entries(actions).forEach(([action, value]) => {
          if (value) {
            permissionArray.push(`${module.toUpperCase()}_${action.toUpperCase()}`);
          }
        });
      });
    }

    // System permissions
    if (permissions.system) {
      Object.entries(permissions.system).forEach(([module, actions]) => {
        Object.entries(actions).forEach(([action, value]) => {
          if (value) {
            permissionArray.push(`${module.toUpperCase()}_${action.toUpperCase()}`);
          }
        });
      });
    }

    return permissionArray;
  },

  // Convert flat permission array to API format
  convertArrayToPermissions: (permissions: string[]): UserPermissions => {
    const apiPermissions: UserPermissions = {
      employee_management: {
        employees: { view: false, create: false, edit: false, delete: false },
        department: { view: false, create: false, edit: false, delete: false },
        agreement: { view: false, edit: false },
        compensation: { view: false, edit: false }
      },
      sales_management: {
        client: { view: false, create: false, edit: false, delete: false },
        proposal: { view: false, create: false, edit: false, delete: false },
        contract: { view: false, create: false, edit: false, delete: false },
        revenue: { view: false }
      },
      finance_management: {
        invoice: { view: false, create: false, edit: false, delete: false },
        expense: { view: false, create: false, edit: false, delete: false },
        financialReport: { view: false }
      },
      hiring: {
        candidate: { view: false, create: false, edit: false, delete: false },
        jobPosting: { view: false, create: false, edit: false, delete: false },
        onboarding: { view: false, create: false, edit: false, delete: false },
        settings: {
          onboardingTask: { view: false, create: false, edit: false, delete: false }
        }
      },
      project_management: {
        project: { view: false, create: false, edit: false, delete: false },
        task: { view: false, create: false, edit: false, delete: false },
        projectStatus: { view: false, create: false, edit: false, delete: false }
      },
      settings: {
        roleAndPermission: { view: false, create: false, edit: false, delete: false },
        jobTitle: { view: false, create: false, edit: false, delete: false },
      },
      system: {
        system: { admin: false }
      }
    };

    permissions.forEach(permission => {
      const parts = permission.toLowerCase().split('.');
      const [module, submodule, action] = parts;

      if (parts.length === 2) {
        // Handle submodule.action format (e.g., role.view)
        const [targetSub, targetAction] = [module, submodule];

        if (targetSub === 'role' || targetSub === 'permission' || targetSub === 'employees' || targetSub === 'user') {
          const uiAction = targetAction === 'manage' ? 'edit' : targetAction;
          if (apiPermissions.settings.roleAndPermission) {
            (apiPermissions.settings.roleAndPermission as any)[uiAction] = true;
          }
        }

        // Standard mappings
        if (targetSub === 'employees' || targetSub === 'user') {
          const action = targetAction;
          if (apiPermissions.employee_management.employees) (apiPermissions.employee_management.employees as any)[action] = true;
          if (targetSub === 'user' && apiPermissions.settings.roleAndPermission) (apiPermissions.settings.roleAndPermission as any)[action] = true;
        } else if (targetSub === 'role') {
          if (apiPermissions.settings.roleAndPermission) (apiPermissions.settings.roleAndPermission as any)[targetAction] = true;
        } else if (targetSub === 'permission') {
          if (apiPermissions.settings.roleAndPermission) (apiPermissions.settings.roleAndPermission as any)[targetAction] = true;
        } else if (targetSub === 'candidate') {
          if (apiPermissions.hiring.candidate) (apiPermissions.hiring.candidate as any)[targetAction] = true;
        } else if (targetSub === 'jobposting') {
          if (apiPermissions.hiring.jobPosting) (apiPermissions.hiring.jobPosting as any)[targetAction] = true;
        } else if (targetSub === 'onboarding') {
          if (apiPermissions.hiring.onboarding) (apiPermissions.hiring.onboarding as any)[targetAction] = true;
        } else if (targetSub === 'jobtitle') {
          if (apiPermissions.settings.jobTitle) (apiPermissions.settings.jobTitle as any)[targetAction] = true;
        }
      } else if (parts.length === 3) {
        // Handle module.submodule.action format
        if (module === 'settings' && submodule === 'roleandpermission') {
          (apiPermissions.settings.roleAndPermission as any)[action] = true;
        } else if (module === 'settings' && submodule === 'role') {
          (apiPermissions.settings.roleAndPermission as any)[action] = true;
        } else if (module === 'employee_management' && submodule === 'employees') {
          (apiPermissions.employee_management.employees as any)[action] = true;
          (apiPermissions.settings.roleAndPermission as any)[action] = true;
        }
      }
    });

    return apiPermissions;
  },
}; 